import AddCircleIcon from "@mui/icons-material/AddCircle";
import { GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DateFormatter,
  DateTimeFormats,
} from "../../../../shared/utils/DateFormatter";
import useGet from "../../../../services/hooks/useGet";
import usePost from "../../../../services/hooks/usePost";
import {
  hideLoader,
  showLoader,
} from "../../../../store/currentPreferencesSlice";
import { apiEndpoints } from "../../../../shared/constants/api-constant";
import { Heading } from "../../../../shared/ReusableComponents";
import IconButton from "../../../../shared/ReusableComponents/IconButton";
import { GridTable } from "../../../../shared/ReusableComponents/GridTable";
import NurseInitiateDialog from "./NurseInitiateDialog";
import { INurseData } from "../../../../shared/Interfaces";
import moment from "moment";
import { CommonFunctions } from "../../../../shared/utils/CommonFunctions";
import { AppConfigurationHelper } from "../../../../services/AppConfigurationHelper";

function NurseInitiated({ caseId }: { caseId: number }) {
  const columns: GridColDef[] = [
    {
      field: "createdOn",
      headerName: "Date, Time",
      width: 180,
      sortable: true,
      valueGetter: (value, row) =>
        `${DateFormatter.formatDate(value)}, ${DateFormatter.formatTime(
          row.createdOn
        )}`,
    },
    {
      field: "medicineName",
      headerName: "Medicine",
      sortable: true,
    },
    {
      field: "route",
      headerName: "Route",
    },
    {
      field: "dose",
      headerName: "Dose",
    },
    {
      field: "dateOfDose",
      headerName: "Date of Dose",
    },
    {
      field: "timeOfDose",
      headerName: "Time of Dose",
    },
    {
      field: "initiator",
      headerName: "Nurse Initiator",
      valueGetter: (value, row) =>
        CommonFunctions.getDisplayValue(row.initiator),
    },
    {
      field: "counterSignedBy",
      headerName: "Counter Signed By",
      valueGetter: (value, row) => {
        const displayName = CommonFunctions.getDisplayValue(
          row.counterSignedBy
        );
        return displayName ===
          AppConfigurationHelper.Instance().displayDefaults?.emptyFallback
          ? displayName
          : `${displayName} (${CommonFunctions.nameFormats.rn})`;
      },
    },
  ];

  const fields = [
    { label: "Medicine", key: "medicineName" },
    { label: "Route", key: "route" },
    { label: "Dose", key: "dose" },
    {
      label: "Date of Dose",
      key: "dateOfDose",
      readonly: true,
    },
    {
      label: "Time of Dose",
      key: "timeOfDose",
      readonly: true,
    },
    { label: "Nurse Initials (SN)", key: "initiator" },
    {
      label: "Counter Signed By (RN)",
      key: "counterSignedBy",
    },
  ];

  const auth = useSelector((state: any) => state.login);
  const initialForm = {
    caseId: caseId,
    medicineName: "",
    dose: "",
    route: "",
    dateOfDose: DateFormatter.formatDate(new Date()),
    timeOfDose: DateFormatter.formatTime(new Date()),
    initiator: CommonFunctions.formatFirstAndLastName(
      auth.userInfo?.userFirstName,
      auth.userInfo?.userLastName,
      { suffixKey: "sn" }
    ),
    counterSignedBy: "",
  };

  const [open, setOpen] = useState(false);
  const [originalData, setOriginalData] = useState<INurseData[]>([]);
  const [formData, setFormData] = useState({
    ...initialForm,
    caseId,
  });

  const dispatch = useDispatch();
  const { postService } = usePost();
  const {
    data: getData,
    getService: getApiCall,
    loading: getLoading,
  } = useGet();

  const handleSubmit = () => {
    dispatch(showLoader());
    formData.dateOfDose = moment
      .utc(formData.dateOfDose, DateTimeFormats.DateFormat)
      .toISOString();
    formData.timeOfDose = DateFormatter.formatUTCTime(formData.timeOfDose);
    const message = `${formData.medicineName} has been successfully added.`;

    postService(apiEndpoints.NURSEINITIATED, formData, message).then(() => {
      getApiCall(`${apiEndpoints.NURSEINITIATED}/${caseId}`);
    });

    setFormData({
      ...initialForm,
      caseId: caseId,
    });
    setOpen(false);
    dispatch(hideLoader());
  };

  const handleClose = () => {
    setFormData(initialForm);
    setOpen(false);
  };

  useEffect(() => {
    getLoading && dispatch(showLoader());
    !getLoading && dispatch(hideLoader());
  }, [getLoading]);

  useEffect(() => {
    getLoading && dispatch(showLoader());
    if (!(getData && originalData)) {
      getApiCall(`${apiEndpoints.NURSEINITIATED}/${caseId}`);
    }
  }, [caseId]);

  useEffect(() => {
    if (getData?.length > 0) {
      const result = getData.map((item: INurseData) => ({
        ...item,
        dateOfDose: DateFormatter.formatDate(item.dateOfDose),
        timeOfDose: DateFormatter.formatTime(item.timeOfDose, true),
      }));
      setOriginalData(result);
      dispatch(hideLoader());
    }
  }, [getData]);

  return (
    <div>
      <div className="headerDiv">
        <div className="iconHeading">
          <Heading type="h5" isBold={true} text="Prescription" />
          <IconButton
            className="iconBtn"
            onClick={() => setOpen(true)}
            text="Add New"
            icon={<AddCircleIcon className="addPlus-icon" fill="#fff" />}
          />
        </div>
      </div>

      <React.Fragment>
        <GridTable
          rows={originalData}
          columns={columns}
          pageSizeOptions={[5, 10, 15]}
          initialPageSize={10}
          showSearchBar={false}
        />
        <NurseInitiateDialog
          open={open}
          onClose={handleClose}
          onSubmit={handleSubmit}
          title="Add Prescription"
          caption=" (All fields in this form are mandatory)"
          formData={formData}
          setFormData={setFormData}
          fields={fields}
        />
      </React.Fragment>
    </div>
  );
}

export default NurseInitiated;
